import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Trash2, FolderInput, PlusCircle, MoreHorizontal, ChevronDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent
} from '../ui/dropdown-menu';
import { useToast } from '../ui/use-toast';
import { 
  getGmailLabels, 
  moveEmailsToFolder, 
  getThreadLabels, 
  GmailLabel 
} from '../../services/google/gmail';
import { ScrollArea } from '../ui/scroll-area';
import { TaskAssociationModal } from './TaskAssociationModal';

interface EmailActionsProps {
  emailIds: string[];
  threadId?: string;
  emailSubject?: string;
  onSuccess?: () => void;
  compact?: boolean;
}

export const EmailActions: React.FC<EmailActionsProps> = ({ 
  emailIds,
  threadId,
  emailSubject = '',
  onSuccess, 
  compact = false 
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState<GmailLabel[]>([]);
  const [threadLabels, setThreadLabels] = useState<string[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  
  // Charger les labels au chargement du composant
  useEffect(() => {
    const fetchLabels = async () => {
      setLoadingLabels(true);
      try {
        const fetchedLabels = await getGmailLabels();
        
        // Si on a un threadId, récupérer ses labels associés
        let threadLabelIds: string[] = [];
        if (threadId) {
          threadLabelIds = await getThreadLabels(threadId);
          setThreadLabels(threadLabelIds);
        }
        
        // Filtrer les labels système que nous ne voulons pas montrer
        const filteredLabels = fetchedLabels.filter(label => 
          label.type !== 'system' || 
          ['TRASH', 'SPAM', 'SENT', 'IMPORTANT'].includes(label.id)
        );
        
        // Trier les labels: d'abord ceux du thread, puis les spéciaux, puis par ordre alphabétique
        const sortedLabels = filteredLabels.sort((a, b) => {
          // Si les deux labels appartiennent au thread ou aucun des deux, continuer avec les autres critères
          const aInThread = threadLabelIds.includes(a.id);
          const bInThread = threadLabelIds.includes(b.id);
          
          if (aInThread && !bInThread) return -1;
          if (!aInThread && bInThread) return 1;
          
          // Pour les labels qui ne sont pas du thread ou les deux le sont, trier par type spécial puis alphabétique
          const specialLabels = ['TRASH', 'SPAM', 'SENT', 'IMPORTANT'];
          const aIsSpecial = specialLabels.includes(a.id);
          const bIsSpecial = specialLabels.includes(b.id);
          
          if (aIsSpecial && !bIsSpecial) return -1;
          if (!aIsSpecial && bIsSpecial) return 1;
          if (aIsSpecial && bIsSpecial) {
            return specialLabels.indexOf(a.id) - specialLabels.indexOf(b.id);
          }
          
          // Tri alphabétique pour les autres labels
          return a.name.localeCompare(b.name);
        });
        
        setLabels(sortedLabels);
      } catch (error) {
        console.error('Erreur lors du chargement des labels:', error);
      } finally {
        setLoadingLabels(false);
      }
    };

    fetchLabels();
  }, [threadId]);

  const handleAction = async (action: string, labelId?: string) => {
    setLoading(true);
    
    try {
      switch (action) {
        case 'moveToFolder':
          if (!labelId) {
            throw new Error('Label ID non fourni');
          }
          
          await moveEmailsToFolder(emailIds, labelId);
          toast({
            title: t('emails.moved', 'Emails déplacés'),
            description: t('emails.movedDescription', 'Les emails ont bien été déplacés'),
          });
          break;
        
        case 'delete':
          // Pour supprimer, on déplace vers la corbeille (TRASH)
          await moveEmailsToFolder(emailIds, 'TRASH');
          toast({
            title: t('emails.deleted', 'Emails supprimés'),
            description: t('emails.deletedDescription', 'Les emails ont bien été supprimés'),
          });
          break;
          
        case 'associate':
          // Ouvrir le modal d'association de tâche au lieu d'appeler directement la fonction
          setTaskModalOpen(true);
          break;
          
        default:
          break;
      }
      
      if (action !== 'associate' && onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error(`Error performing action ${action}:`, error);
      toast({
        variant: 'destructive',
        title: t('emails.actionFailed', 'Échec de l\'action'),
        description: t('emails.actionFailedDescription', 'Une erreur est survenue lors de l\'exécution de l\'action'),
      });
    } finally {
      if (action !== 'associate') {
        setLoading(false);
      }
    }
  };

  const renderLabelItem = (label: GmailLabel) => {
    const isThreadLabel = threadLabels.includes(label.id);
    
    return (
      <DropdownMenuItem 
        key={label.id} 
        onClick={() => handleAction('moveToFolder', label.id)}
        className={isThreadLabel ? "font-bold bg-blue-50 dark:bg-blue-900/20" : ""}
      >
        <div 
          className={`w-3 h-3 rounded-full mr-2 ${isThreadLabel ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
          style={label.color ? { backgroundColor: label.color.backgroundColor } : undefined}
        />
        {label.name}
        {isThreadLabel && (
          <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
            {t('emails.threadLabel', 'Dans la conversation')}
          </span>
        )}
      </DropdownMenuItem>
    );
  };

  const renderMoveToFolderMenu = () => {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <FolderInput className="mr-2 h-4 w-4" />
          <span>{t('emails.moveToFolder', 'Déplacer vers')}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent className="w-72">
            {loadingLabels ? (
              <DropdownMenuItem disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading', 'Chargement...')}
              </DropdownMenuItem>
            ) : labels.length === 0 ? (
              <DropdownMenuItem disabled>
                {t('emails.noFolders', 'Aucun dossier disponible')}
              </DropdownMenuItem>
            ) : (
              <ScrollArea className="h-72">
                <div className="p-1">
                  {threadLabels.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        {t('emails.threadLabels', 'Labels de la conversation')}
                      </div>
                      {labels.filter(label => threadLabels.includes(label.id)).map(renderLabelItem)}
                      <DropdownMenuSeparator />
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        {t('emails.otherLabels', 'Autres labels')}
                      </div>
                    </>
                  )}
                  {labels.filter(label => !threadLabels.includes(label.id)).map(renderLabelItem)}
                </div>
              </ScrollArea>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    );
  };

  // Version compacte pour l'affichage dans un tableau
  if (compact) {
    return (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('emails.actions', 'Actions')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {renderMoveToFolderMenu()}
            
            <DropdownMenuItem onClick={() => handleAction('delete')}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('emails.delete', 'Supprimer')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleAction('associate')}>
              <PlusCircle className="mr-2 h-4 w-4" />
              {t('emails.associateTask', 'Associer à une tâche')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Modal d'association de tâche */}
        <TaskAssociationModal
          isOpen={taskModalOpen}
          onClose={() => setTaskModalOpen(false)}
          onSuccess={() => {
            if (onSuccess) onSuccess();
            setLoading(false);
          }}
          emailId={emailIds[0]}
          emailSubject={emailSubject}
        />
      </>
    );
  }

  // Version normale avec boutons complets
  return (
    <>
      <div className="flex space-x-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={loading}
            >
              <FolderInput className="mr-2 h-4 w-4" />
              {t('emails.moveToFolder', 'Déplacer vers')}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            {loadingLabels ? (
              <DropdownMenuItem disabled>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading', 'Chargement...')}
              </DropdownMenuItem>
            ) : (
              <ScrollArea className="h-72">
                <div className="p-1">
                  {threadLabels.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        {t('emails.threadLabels', 'Labels de la conversation')}
                      </div>
                      {labels.filter(label => threadLabels.includes(label.id)).map(renderLabelItem)}
                      <DropdownMenuSeparator />
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        {t('emails.otherLabels', 'Autres labels')}
                      </div>
                    </>
                  )}
                  {labels.filter(label => !threadLabels.includes(label.id)).map(renderLabelItem)}
                </div>
              </ScrollArea>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => handleAction('delete')}
          disabled={loading}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('emails.delete', 'Supprimer')}
        </Button>
        
        <Button 
          variant="default" 
          size="sm" 
          onClick={() => handleAction('associate')}
          disabled={loading}
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          {t('emails.associateTask', 'Associer à une tâche')}
        </Button>
      </div>
      
      {/* Modal d'association de tâche */}
      <TaskAssociationModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSuccess={() => {
          if (onSuccess) onSuccess();
          setLoading(false);
        }}
        emailId={emailIds[0]}
        emailSubject={emailSubject}
      />
    </>
  );
};
